import { api, type Product, type SearchItem } from "../api";
import { searchItems, type SearchFields } from "../search";

/**
 * One shared, unpaginated product catalog for local search.
 * Fetched WITHOUT `q` → no per-keystroke `/api/products` / `/api/items/search` scan.
 * ponytail: single in-memory snapshot per page session; refresh via loadProducts
 * (focus/resync/explicit refresh) instead of a cache TTL.
 */
export const CATALOG_LIMIT = 2000;

let catalog: Product[] | null = null;
let pending: Promise<Product[]> | null = null;

/** Non-empty snapshot, or null (cold/failed) so callers fall back to the server. */
export function cachedCatalog(): Product[] | null {
  return catalog && catalog.length > 0 ? catalog : null;
}

/** Seed/refresh the shared snapshot (caller already fetched a full, unfiltered list). */
export function setCachedCatalog(products: Product[]): void {
  catalog = products;
}

export function loadCatalog(): Promise<Product[]> {
  const cached = cachedCatalog();
  if (cached) return Promise.resolve(cached);
  if (pending) return pending;
  pending = api
    .products({ limit: CATALOG_LIMIT, include_total: 0 })
    .then((result) => {
      const list = result.products || [];
      if (list.length > 0) catalog = list;
      return list;
    })
    .finally(() => {
      pending = null;
    });
  return pending;
}

/**
 * Canonical row + pre-merge values in the same slots, so a merged-away product
 * is still found by its old name/SKU/barcode (no extra scoring fields).
 */
export function catalogSearchFields(p: Product): SearchFields {
  const join = (canonical: string | null | undefined, alias: string[] | undefined) =>
    [canonical || "", ...(alias || [])].filter(Boolean).join(" ");
  return {
    id: p.id,
    name: join(p.item_name, p.alias_item_names),
    sku: join(p.sku, p.alias_skus),
    barcode: join(p.barcode, p.alias_barcodes),
  };
}

/**
 * Ranked search over the shared catalog, shaped like `/api/items/search`.
 * Returns null when no catalog is available so callers fall back to the server.
 */
export async function searchCatalog(query: string, limit: number): Promise<SearchItem[] | null> {
  let list: Product[];
  try {
    list = await loadCatalog();
  } catch {
    return null;
  }
  if (list.length === 0) return null;
  return searchItems(
    list,
    query,
    catalogSearchFields,
    { mode: "and" },
  )
    .slice(0, limit)
    .map(({ item }) => ({
      id: item.id,
      item_name: item.item_name,
      sku: item.sku ?? undefined,
      default_price: item.default_price ?? undefined,
      cost: item.cost ?? undefined,
      category_name: item.category_name ?? undefined,
    }));
}
